import React, { useCallback } from 'react';
import { TRANSITION_STROKE_WIDTH } from './ellipsis.tsx';
import { EDGE_TYPE } from './directionKeys.tsx';
import type { ValidationFlag, Connection } from './toPortInfo.tsx';
import type { ItemReference } from './ListEdgeDetails.tsx';
import { DiagramArrow } from './DiagramArrow.tsx';
import { useStrokeColor } from './useStrokeColor.tsx';

export type Props = {
	id: string;
	flag?: ValidationFlag;
	isHovered?: boolean;
	isHighlighted?: boolean;
	isImmutable?: boolean;
	isSelected?: boolean;
	isTargeted?: boolean;
	connections: Connection[] | undefined;
	onSelect?: (item: ItemReference | undefined) => void;
	onHover?: (item: ItemReference | undefined) => void;
};

const SharedTransition = ({
	id,
	flag,
	isHovered = false,
	isHighlighted = false,
	isImmutable = false,
	isSelected = false,
	connections,
	onSelect,
	onHover,
}: Props): React.JSX.Element | null => {
	const stroke = useStrokeColor({
		flag,
		isHighlighted,
		isHovered,
		isSelected,
	});

	const onGroupClick = useCallback(() => {
		if (!isImmutable) {
			onSelect?.({ type: EDGE_TYPE, id });
		}
	}, [isImmutable, onSelect, id]);

	const onMouseEnter = useCallback(
		(connection: Connection) => () => {
			onHover?.({ id, type: EDGE_TYPE, connection });
		},
		[id, onHover],
	);

	const onMouseLeave = useCallback(() => {
		onHover?.(undefined);
	}, [onHover]);

	if (!connections) {
		return null;
	}

	return (
		<>
			{connections.map(({ path, targetPort }, i) => (
				<g
					onClick={onGroupClick}
					onMouseEnter={onMouseEnter(connections[i])}
					onMouseLeave={onMouseLeave}
					key={i}
				>
					{/* Click and hover area */}
					<path fill="none" stroke="transparent" d={path} strokeLinejoin="round" strokeWidth="15" />
					{/* Routed path of transition */}
					<path
						fill="none"
						stroke={stroke}
						d={path}
						strokeLinejoin="round"
						strokeWidth={TRANSITION_STROKE_WIDTH}
					/>
					<DiagramArrow
						portIndex={targetPort.portIndex}
						portPosition={targetPort.position}
						stroke={stroke}
					/>
				</g>
			))}
		</>
	);
};

export default SharedTransition;
